import React from "react";
import {useTranslation} from "react-i18next";
import {faCartPlus, faFireFlameCurved} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "./RentPageList.styles"
import ActionButton from "../../lib/UIBookBrd/ActionButton";
import Stepper from "../../lib/UIBookBrd/Stepper";
import useBasket from "../../lib/Hooks/useBasket";
import {getProductIcon, getProductName, hasNumericPrice, ProductValue} from "../../lib/products";

type ProductCardProps = {
    id: string
    value: ProductValue
    index?: number
}

const LOW_STOCK = 2

const ProductCard = ({id, value, index = 0}: ProductCardProps) => {

    const {t} = useTranslation()

    const {getCount, addItem, removeItem} = useBasket()

    const {
        image,
        producent,
        count = 0,
        rentPrice,
        type,
        isSpecialPrice
    } = value

    const inBasket = getCount(id)

    const onAdd = (event: React.MouseEvent) => {
        event.stopPropagation()
        addItem(id, count)
    }

    const onRemove = (event: React.MouseEvent) => {
        event.stopPropagation()
        removeItem(id)
    }

    return (
        <Styled.Card
            initial={{opacity: 0, y: 22}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, margin: '-40px'}}
            transition={{duration: 0.5, delay: Math.min(index, 8) * 0.04, ease: [0.16, 1, 0.3, 1]}}
        >
            <Styled.CardMedia>
                <Styled.CardImage
                    src={image}
                    alt={id}
                    fit={'contain'}
                    icon={getProductIcon(type)}
                />
                {type && (
                    <Styled.TypeChip>
                        <Styled.TypeChipIcon icon={getProductIcon(type)}/>
                        {type}
                    </Styled.TypeChip>
                )}
                {isSpecialPrice && (
                    <Styled.HotBadge>
                        <Styled.HotBadgeIcon icon={faFireFlameCurved}/>
                        {t('lbl_hot_price')}
                    </Styled.HotBadge>
                )}
            </Styled.CardMedia>

            <Styled.CardBody>
                <Styled.CardProducer>
                    {producent ?? 'S115'}
                </Styled.CardProducer>
                <Styled.CardName>
                    {getProductName(id, value, t)}
                </Styled.CardName>
                <Styled.CardMeta>
                    {hasNumericPrice(rentPrice) ? (
                        <>
                            <Styled.CardPrice $highlight={isSpecialPrice}>
                                {`${rentPrice} PLN`}
                            </Styled.CardPrice>
                            <Styled.CardPriceUnit>
                                {t('lbl_rent_day')}
                            </Styled.CardPriceUnit>
                        </>
                    ) : (
                        <Styled.CardPrice>
                            {t(rentPrice as string)}
                        </Styled.CardPrice>
                    )}
                    <Styled.Availability $low={count <= LOW_STOCK}>
                        {`${count} ${t('lbl_pieces_short')}`}
                    </Styled.Availability>
                </Styled.CardMeta>
            </Styled.CardBody>

            <Styled.CardFooter>
                <Styled.CardAction>
                    {inBasket > 0 ? (
                        <Stepper
                            size={'sm'}
                            fullWidth={true}
                            unit={t('lbl_pieces_short')}
                            value={inBasket}
                            max={count}
                            removeAtMin={true}
                            decreaseLabel={t('lbl_remove')}
                            increaseLabel={t('lbl_add_to_basket')}
                            onDecrease={onRemove}
                            onIncrease={onAdd}
                        />
                    ) : (
                        <ActionButton
                            size={'sm'}
                            fullWidth={true}
                            icon={faCartPlus}
                            label={t('lbl_add_to_basket')}
                            onClick={onAdd}
                        />
                    )}
                </Styled.CardAction>
            </Styled.CardFooter>
        </Styled.Card>
    )
}

export default ProductCard
